import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Table({ cols, rows, testid }) {
  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="bg-cloud border-b border-border">{cols.map((c) => <th key={c.k} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{c.h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              {cols.map((c) => <td key={c.k} className="px-3 py-2 text-sm text-slate">{c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "—")}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="text-center py-6 text-mutedsea text-sm">No data.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function BOIReports() {
  const [tab, setTab] = useState("funnel");
  const [data, setData] = useState({});
  const [convBy, setConvBy] = useState("business_line");

  const fetchReport = useCallback(async (key, url) => {
    try { const { data } = await api.get(url); setData((d) => ({ ...d, [key]: data })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    const map = {
      funnel: "/boi/reports/funnel", aging: "/boi/reports/aging", top: "/boi/reports/top-relationships",
      dq: "/boi/reports/data-quality", dnc: "/boi/reports/dnc", signals: "/boi/reports/signals",
      score: "/boi/reports/score-outcome", disp: "/boi/reports/dispositions", apollo: "/boi/reports/apollo",
      followup: "/boi/reports/followup-compliance",
    };
    if (tab === "conversion") fetchReport("conversion", `/boi/reports/conversion?by=${convBy}`);
    else if (map[tab]) fetchReport(tab, map[tab]);
  }, [tab, convBy, fetchReport]);

  const exportCsv = (report) => window.open(`${BACKEND}/api/boi/reports/export?report=${report}`, "_blank");

  return (
    <div data-testid="boi-reports-page">
      <PageHeader title="BOI Reports" subtitle="Funnel, conversion, value, aging, compliance and signal analysis — scoped to your book.">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportCsv("relationships")} data-testid="export-relationships"><Download className="w-4 h-4 mr-1" />Relationships CSV</Button>
          <Button variant="outline" onClick={() => exportCsv("pipeline")} data-testid="export-pipeline"><Download className="w-4 h-4 mr-1" />Pipeline CSV</Button>
        </div>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="reports-tabs">
          {[["funnel", "Funnel"], ["conversion", "Conversion"], ["disp", "Dispositions"], ["followup", "Follow-Up"],
            ["aging", "Pipeline Aging"], ["top", "Top Relationships"], ["dq", "Data Quality"], ["dnc", "DNC"],
            ["apollo", "Apollo"], ["score", "Score→Outcome"], ["signals", "Signals"]].map(([v, l]) =>
            <TabsTrigger key={v} value={v} data-testid={`report-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="funnel" className="mt-4">
          <Table testid="funnel-table" cols={[{ k: "step", h: "Stage" }, { k: "count", h: "Count" }, { k: "conversion_pct", h: "Conversion %", fmt: (v) => `${v}%` }]} rows={data.funnel?.funnel || []} />
        </TabsContent>

        <TabsContent value="conversion" className="mt-4">
          <div className="mb-2 flex gap-2 items-center">
            <span className="text-sm text-mutedsea">Group by:</span>
            <select className="h-9 border border-border rounded-sm px-2 text-sm" value={convBy} onChange={(e) => setConvBy(e.target.value)} data-testid="conversion-by">
              {["business_line", "icp", "industry", "source"].map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
            </select>
          </div>
          <Table testid="conversion-table" cols={[{ k: "group", h: "Group" }, { k: "relationships", h: "Relationships" }, { k: "opportunities", h: "Opps" }, { k: "wins", h: "Wins" }, { k: "conversion_pct", h: "Conv %", fmt: (v) => `${v}%` }, { k: "pipeline_value", h: "Pipeline", fmt: money }, { k: "estimated_value", h: "Est. Value", fmt: money }]} rows={data.conversion?.rows || []} />
        </TabsContent>

        <TabsContent value="disp" className="mt-4"><Table testid="disp-table" cols={[{ k: "disposition", h: "Disposition" }, { k: "count", h: "Count" }]} rows={data.disp?.rows || []} /></TabsContent>
        <TabsContent value="followup" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="followup-cards">
            {data.followup && Object.entries({ "With Follow-Up": data.followup.with_followup, "Due Today": data.followup.due_today, "Overdue": data.followup.overdue, "No Next Action": data.followup.no_next_action, "On Track": data.followup.on_track }).map(([l, v]) => (
              <div key={l} className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-2xl text-navy">{v}</div><div className="text-xs uppercase text-mutedsea">{l}</div></div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="aging" className="mt-4"><Table testid="aging-table" cols={[{ k: "range", h: "Age (days)" }, { k: "count", h: "Opportunities" }, { k: "value", h: "Value", fmt: money }]} rows={data.aging?.buckets || []} /></TabsContent>
        <TabsContent value="top" className="mt-4"><Table testid="top-table" cols={[{ k: "name", h: "Relationship" }, { k: "rank_score", h: "Rank" }, { k: "highest_score", h: "Score" }, { k: "estimated_value", h: "Est. Value", fmt: money }, { k: "best_next_opportunity", h: "Best Opportunity" }, { k: "why", h: "Why", fmt: (v) => (v || []).join(" · ") }]} rows={data.top?.rows || []} /></TabsContent>
        <TabsContent value="dq" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="dq-cards">
            {data.dq && Object.entries(data.dq).map(([k, v]) => (
              <div key={k} className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-2xl text-seaamber">{v}</div><div className="text-xs uppercase text-mutedsea">{k.replace(/_/g, " ")}</div></div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="dnc" className="mt-4">
          <p className="text-sm text-mutedsea mb-2">{data.dnc?.relationship_count ?? 0} suppressed relationships · {data.dnc?.contact_count ?? 0} suppressed contacts. {data.dnc?.note}</p>
          <Table testid="dnc-table" cols={[{ k: "name", h: "Relationship" }, { k: "relationship_status", h: "Status" }, { k: "updated_by", h: "By" }, { k: "updated_at", h: "When", fmt: (v) => (v || "").slice(0, 10) }]} rows={data.dnc?.relationships || []} />
        </TabsContent>
        <TabsContent value="apollo" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="apollo-cards">
            {data.apollo && Object.entries({ "Relationships Imported": data.apollo.relationships_imported, "Credits Consumed": data.apollo.credits_consumed, "Enrichments": data.apollo.enrichments, "Opps Created": data.apollo.opportunities_created, "Wins": data.apollo.wins, "Credits / Relationship": data.apollo.credits_per_relationship ?? "—", "Credits / Opportunity": data.apollo.credits_per_opportunity ?? "—" }).map(([l, v]) => (
              <div key={l} className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-2xl text-navy">{v}</div><div className="text-xs uppercase text-mutedsea">{l}</div></div>
            ))}
          </div>
          <p className="text-xs text-mutedsea mt-2">{data.apollo?.note}</p>
        </TabsContent>
        <TabsContent value="score" className="mt-4">
          <Table testid="score-table" cols={[{ k: "score_range", h: "Score Range" }, { k: "relationships", h: "Relationships" }, { k: "opportunities", h: "Opps" }, { k: "wins", h: "Wins" }, { k: "opportunity_rate", h: "Opp Rate", fmt: (v) => `${v}%` }, { k: "win_rate", h: "Win Rate", fmt: (v) => `${v}%` }]} rows={data.score?.rows || []} />
          <p className="text-xs text-mutedsea mt-2">{data.score?.note}</p>
        </TabsContent>
        <TabsContent value="signals" className="mt-4">
          <Table testid="signals-table" cols={[{ k: "signal", h: "Signal" }, { k: "present_in", h: "Present In" }, { k: "with_opportunity", h: "With Opportunity" }, { k: "association_pct", h: "Association %", fmt: (v) => `${v}%` }]} rows={data.signals?.rows || []} />
          <p className="text-xs text-mutedsea mt-2">{data.signals?.note}</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
